import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  FileText,
  Database,
  ArrowLeftRight,
  Palette,
  LayoutTemplate,
  Menu,
} from 'lucide-react';
import { api } from '../lib/api.ts';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface CmsSpace {
  id: string;
  name: string;
  slug: string;
  domain: string | null;
  type: string;
  createdAt: string;
  updatedAt: string;
}

// ─── Tab card ─────────────────────────────────────────────────────────────────

function SpaceTabCard({
  icon: Icon,
  title,
  description,
  to,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  to: string;
}) {
  const navigate = useNavigate();
  return (
    <button
      onClick={() => navigate(to)}
      className="flex items-start gap-4 p-5 bg-white border border-zinc-200 rounded-xl hover:border-zinc-300 hover:shadow-sm transition-all text-left w-full group"
    >
      <div className="flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg bg-zinc-100 text-zinc-500 group-hover:bg-zinc-900 group-hover:text-white transition-colors">
        <Icon size={18} />
      </div>
      <div>
        <div className="font-medium text-zinc-900 text-sm">{title}</div>
        <div className="text-xs text-zinc-400 mt-0.5">{description}</div>
      </div>
    </button>
  );
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsSpaceDetailPage() {
  const { spaceId } = useParams<{ spaceId: string }>();

  const { data: space, isLoading } = useQuery<CmsSpace>({
    queryKey: ['cms-space', spaceId],
    queryFn: () => api.get(`/api/v1/cms/spaces/${spaceId}`).then((r) => r.data),
    enabled: !!spaceId,
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-48 bg-zinc-100 rounded animate-pulse" />
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={3} />
        </div>
      </div>
    );
  }

  if (!space) {
    return (
      <div className="text-center py-16 text-zinc-400">
        <p className="text-sm">Space not found.</p>
        <Link to="/spaces" className="text-xs text-zinc-500 underline mt-2 inline-block">
          Back to spaces
        </Link>
      </div>
    );
  }

  const base = `/spaces/${space.id}`;

  return (
    <div className="space-y-6">
      {/* Section cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <SpaceTabCard
          icon={FileText}
          title="Pages"
          description="Build and publish pages with drag-and-drop blocks"
          to={`${base}/pages`}
        />
        <SpaceTabCard
          icon={Database}
          title="Collections"
          description="Structured content types with custom fields and entries"
          to={`${base}/collections`}
        />
        <SpaceTabCard
          icon={Menu}
          title="Menus"
          description="Manage header, footer and sidebar navigation menus"
          to={`${base}/menus`}
        />
        <SpaceTabCard
          icon={LayoutTemplate}
          title="Layouts"
          description="Define page layouts — width, header style, nav and footer"
          to={`${base}/layouts`}
        />
        <SpaceTabCard
          icon={ArrowLeftRight}
          title="Redirects"
          description="Manage URL slug redirects (301/302)"
          to={`${base}/redirects`}
        />
        <SpaceTabCard
          icon={Palette}
          title="Themes"
          description="Create and manage named themes — colors, fonts, spacing"
          to={`${base}/settings`}
        />
      </div>
    </div>
  );
}
